import { useMemo } from "react";

import { useAtom } from "jotai";
import moment from "moment";

import { useUser } from "@components/user-context";
import { useAdoptedBudgetMix } from "@features/mmm/hooks/use-adopted-budget-mix";
import { efficiencyKpiManager } from "@features/mmm/utils/efficiency-kpi-manager";

import { ClientSettingsAtom, useGlobalMMMStore } from "../mmm-provider";

import { useAdoptedMix } from "./use-adopted-mix";
import { useClientKpis } from "./use-client-kpis";
import { useClientSettingData } from "./use-client-setting-data";
import { useSelectedCycle } from "./use-selected-cycle";

export const useClientSettings = () => {
  const GlobalMMMStore = useGlobalMMMStore();
  const [clientSettings, setClientSettings] = useAtom(ClientSettingsAtom, {
    store: GlobalMMMStore,
  });

  const { user } = useUser();
  const [adoptedMix] = useAdoptedMix();
  const { adoptedBudgetMix } = useAdoptedBudgetMix();
  const { kpis: clientKpis } = useClientKpis();
  const { selectedCycle } = useSelectedCycle();

  const adoptedClientSetting = useMemo(() => {
    return clientSettings.find(
      (clientSetting) => clientSetting.id === adoptedMix?.clientSettingId,
    );
  }, [adoptedMix, clientSettings]);

  const { kpi: adoptedClientSettingKpi } =
    useClientSettingData(adoptedClientSetting);

  const kpi = useMemo(() => {
    let result = adoptedClientSettingKpi;

    if (!result) {
      result = clientKpis.find(
        (e) => e.kpi_name === adoptedBudgetMix?.data.kpi_name,
      );
    }

    return result;
  }, [clientKpis, adoptedClientSettingKpi, adoptedBudgetMix?.data.kpi_name]);

  const percentOfCycleCompleted = useMemo(() => {
    // Add 1 to include the start date in the calculation
    const daysSinceStartOfCycle =
      moment().diff(selectedCycle.date_range.start_date, "days") + 1;
    // Add 1 to include the start date in the calculation
    const totalDaysInCycle =
      moment(selectedCycle.date_range.end_date).diff(
        selectedCycle.date_range.start_date,
        "days",
      ) + 1;
    const percentOfCycle = daysSinceStartOfCycle / totalDaysInCycle;
    return Math.max(0, Math.min(percentOfCycle, 1));
  }, [selectedCycle.date_range]);

  const cycleDateRange = useMemo(
    () => selectedCycle.date_range,
    [selectedCycle.date_range],
  );

  const formattedMixToDateRange = useMemo(() => {
    const endDate = moment(cycleDateRange.end_date).isBefore(moment())
      ? moment(cycleDateRange.end_date)
      : moment();
    return `${moment(cycleDateRange.start_date).format(
      "MMM D",
    )} - ${endDate.format("MMM D")}`;
  }, [cycleDateRange.end_date, cycleDateRange.start_date]);

  const formattedCycleDateRange = useMemo(
    () =>
      `${moment(cycleDateRange.start_date).format("MMM D")} - ${moment(
        cycleDateRange.end_date,
      ).format("MMM D")}`,
    [cycleDateRange.end_date, cycleDateRange.start_date],
  );

  return {
    percentOfCycleCompleted,
    formattedMixToDateRange,
    formattedCycleDateRange,
    clientSettings,
    setClientSettings,
    adoptedClientSetting,
    ...efficiencyKpiManager(kpi, user),
  } as const;
};
